/* iter-ai-help-dialog — Grounded AI help chat with streaming +
 * clickable source chips + voice input + support-ticket escalation.
 *
 * Guardrails enforced client-side (backend also enforces them):
 *   - Never send question when < 3 chars
 *   - Rate-limit indicator when server returns 429
 *   - Prompt-guard warning if user types secret/tax keywords
 *
 * Phase-2 features live in this same file (kept small):
 *   - conversation memory: `conversationId` state carries across turns
 *   - voice input: Web Speech API mic button
 *   - locale: pulled from navigator.language
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { HelpCircle, Send, Mic, Loader2, Sparkles, ExternalLink, ThumbsUp, ThumbsDown, LifeBuoy } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { toast } from 'sonner';
import { useNavigate, useLocation } from 'react-router-dom';
import { useIsSmallPhone } from '@/hooks/useIsMobile';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const FORBIDDEN_HINT = /admin password|secret key|api key|tax advice|refund|legal advice/i;

export default function AiHelpDialog({ open, onOpenChange }) {
  const [turns,   setTurns]   = useState([]);            // {role,text,sources,turn_id}
  const [q,       setQ]       = useState('');
  const [busy,    setBusy]    = useState(false);
  const [convId,  setConvId]  = useState(null);
  const [listening, setListening] = useState(false);
  const navigate = useNavigate();
  const loc = useLocation();
  const bottomRef = useRef(null);
  // Feb-2026 — read once per open so the contextual chip/label matches
  // whichever Loom panel was active when the dialog was launched.
  const [panelKey, setPanelKey] = useState(null);
  useEffect(() => { if (open) setPanelKey(window.__loomCurrentPanel || null); }, [open]);
  const panelLabel = panelKey ? panelKey.replace(/^./, (c) => c.toUpperCase()) : null;

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [turns]);

  const ask = useCallback(async () => {
    if (!q || q.trim().length < 3 || busy) return;
    if (FORBIDDEN_HINT.test(q)) {
      toast.error('That looks like a secrets/legal question — please open a support ticket instead.');
      return;
    }
    setBusy(true);
    const question = q.trim();
    setQ('');
    setTurns((t) => [...t, { role: 'user', text: question }]);
    let assistantText = '';
    let sources = [];
    let suggestTicket = false;
    let turnId = null;
    try {
      const resp = await fetch(`${API}/help/ask`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question,
          current_route:   loc.pathname,
          current_panel:   panelKey,
          conversation_id: convId,
          locale:          (navigator.language || 'en').split('-')[0],
        }),
      });
      if (resp.status === 429) {
        toast.error('AI help rate limit reached — try again later.');
        setBusy(false); return;
      }
      const reader = resp.body.getReader();
      const dec = new TextDecoder();
      let buf = '';
      // Optimistic assistant slot
      setTurns((t) => [...t, { role: 'assistant', text: '', sources: [], turn_id: null }]);
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const frames = buf.split('\n\n');
        buf = frames.pop();
        for (const f of frames) {
          const line = f.trim();
          if (!line.startsWith('data:')) continue;
          try {
            const p = JSON.parse(line.slice(5).trim());
            if (p.delta) {
              assistantText += p.delta;
              setTurns((t) => {
                const nn = [...t];
                nn[nn.length - 1] = { ...nn[nn.length - 1], text: assistantText };
                return nn;
              });
            }
            if (p.done) {
              sources = p.sources || [];
              suggestTicket = !!p.suggest_ticket;
              turnId = p.turn_id || null;
              if (p.conversation_id) setConvId(p.conversation_id);
              setTurns((t) => {
                const nn = [...t];
                nn[nn.length - 1] = { ...nn[nn.length - 1], sources, turn_id: turnId, suggest_ticket: suggestTicket };
                return nn;
              });
            }
          } catch { /* ignore malformed frame */ }
        }
      }
    } catch (e) {
      toast.error('Could not reach the AI help service.');
    } finally { setBusy(false); }
  }, [q, busy, convId, loc.pathname, panelKey]);

  const feedback = async (turnId, thumbs) => {
    if (!turnId) return;
    try {
      await fetch(`${API}/help/feedback`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ turn_id: turnId, thumbs }),
      });
      toast.success('Thanks — feedback logged.');
    } catch {}
  };

  const jumpToSource = (src) => {
    if (!src?.route) return;
    if (src.route.startsWith('/')) navigate(src.route);
    onOpenChange(false);
  };

  const startVoice = () => {
    const Rec = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!Rec) { toast.error('Voice input not supported in this browser.'); return; }
    const rec = new Rec();
    rec.lang = navigator.language || 'en-US';
    rec.interimResults = false;
    rec.onresult = (ev) => setQ(ev.results?.[0]?.[0]?.transcript || '');
    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);
    setListening(true);
    rec.start();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="ai-help-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" /> Loom AI Help
            <span className="text-[10px] font-normal text-muted-foreground">
              grounded on your manual · streaming · Cmd/Ctrl+/
            </span>
          </DialogTitle>
          {panelLabel && (
            <div className="flex items-center gap-1.5 pt-1" data-testid="ai-help-panel-context">
              <span className="rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary">
                Tuned to · {panelLabel === 'Bible' ? 'Character Bible' : panelLabel}
              </span>
            </div>
          )}
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto space-y-3 pr-1">
          {turns.length === 0 && (
            <div className="rounded-md border border-dashed border-border bg-muted/30 p-4 text-[11.5px] text-muted-foreground">
              Ask anything about The Loom — the AI answers from the manual only and cites its sources.
              Never asks for or reveals secrets. Rate-limited to 20/hr, 100/day per user.
              {panelLabel && (
                <button
                  onClick={() => setQ(`What can I do in the ${panelLabel === 'Bible' ? 'Character Bible' : panelLabel} panel?`)}
                  className="mt-2 block rounded-md border border-primary/30 bg-primary/5 px-2 py-1 text-left text-primary hover:bg-primary/10"
                  data-testid="ai-help-panel-suggestion"
                >
                  Try: "What can I do in the {panelLabel === 'Bible' ? 'Character Bible' : panelLabel} panel?"
                </button>
              )}
            </div>
          )}
          {turns.map((t, i) => (
            <div key={i} className={`rounded-md border p-3 text-[12px] ${
              t.role === 'user'
                ? 'border-primary/30 bg-primary/5'
                : 'border-border bg-card'
            }`}>
              <div className="mb-1 text-[9.5px] uppercase tracking-wider text-muted-foreground">
                {t.role === 'user' ? 'You' : 'AI'}
              </div>
              <div className={t.role === 'assistant' && t.text ? '' : 'whitespace-pre-wrap'}>
                {t.role === 'assistant' && t.text ? (
                  <ReactMarkdown
                    components={{
                      p:  ({ children }) => <p className="mb-1.5 whitespace-pre-wrap last:mb-0">{children}</p>,
                      ul: ({ children }) => <ul className="mb-1.5 list-disc space-y-0.5 pl-4">{children}</ul>,
                      ol: ({ children }) => <ol className="mb-1.5 list-decimal space-y-0.5 pl-4">{children}</ol>,
                      strong: ({ children }) => <strong className="font-semibold text-foreground">{children}</strong>,
                    }}
                  >
                    {t.text}
                  </ReactMarkdown>
                ) : (
                  t.text || (busy && i === turns.length - 1 ? <Loader2 className="inline h-3.5 w-3.5 animate-spin" /> : '')
                )}
              </div>
              {t.role === 'assistant' && t.sources?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {t.sources.map((s, j) => (
                    <button key={j} onClick={() => jumpToSource(s)}
                            className="rounded-full border border-border bg-muted/50 px-2 py-0.5 text-[10.5px] hover:bg-muted"
                            data-testid={`ai-help-source-${j}`}>
                      <ExternalLink className="mr-1 inline h-3 w-3" />
                      {s.title}
                    </button>
                  ))}
                </div>
              )}
              {t.role === 'assistant' && t.text && (
                <div className="mt-1 flex items-center gap-1 text-muted-foreground">
                  <button onClick={() => feedback(t.turn_id, 'up')} className="rounded p-1 hover:bg-secondary" data-testid={`ai-help-thumbs-up-${i}`}>
                    <ThumbsUp className="h-3 w-3" />
                  </button>
                  <button onClick={() => feedback(t.turn_id, 'down')} className="rounded p-1 hover:bg-secondary" data-testid={`ai-help-thumbs-down-${i}`}>
                    <ThumbsDown className="h-3 w-3" />
                  </button>
                  {t.suggest_ticket && (
                    <button onClick={() => { window.open('/support?q=' + encodeURIComponent(t.text.slice(0, 200)), '_blank'); }}
                            className="ml-auto flex items-center gap-1 rounded px-1.5 py-0.5 text-[10.5px] text-primary hover:bg-primary/10"
                            data-testid={`ai-help-open-ticket-${i}`}>
                      <LifeBuoy className="h-3 w-3" /> Open ticket
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
          <div ref={bottomRef} />
        </div>
        <div className="flex items-center gap-1.5 pt-2 border-t border-border">
          <Input value={q} onChange={(e) => setQ(e.target.value)}
                 onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && ask()}
                 placeholder="Ask about The Loom…"
                 disabled={busy}
                 className="h-9"
                 data-testid="ai-help-input" />
          <Button size="sm" variant="ghost" onClick={startVoice} disabled={busy || listening}
                  className="h-9 w-9 p-0" title="Voice input"
                  data-testid="ai-help-mic">
            <Mic className={`h-4 w-4 ${listening ? 'text-rose-500 animate-pulse' : ''}`} />
          </Button>
          <Button size="sm" onClick={ask} disabled={busy || q.trim().length < 3}
                  className="h-9 gap-1"
                  data-testid="ai-help-send">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
            Ask
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function AiHelpLauncher() {
  const [open, setOpen] = useState(false);
  const isSmallPhone = useIsSmallPhone();
  const location = useLocation();
  const onLoomApp = location.pathname.startsWith('/app');
  useEffect(() => {
    if (isSmallPhone) return undefined;
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === '/') { e.preventDefault(); setOpen(true); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isSmallPhone]);

  // Feb-2026 — the ManuscriptToolbar now houses an AI Help button inside
  // The Loom workspace. Listen for its dispatch event so both the toolbar
  // button and the keyboard shortcut open the same dialog.
  useEffect(() => {
    const onToolbarClick = () => setOpen(true);
    window.addEventListener('loom:open-ai-help', onToolbarClick);
    return () => window.removeEventListener('loom:open-ai-help', onToolbarClick);
  }, []);

  if (isSmallPhone) return null;

  return (
    <>
      {/* Feb-2026 — the Manual pill remains when inside The Loom app */}
      {onLoomApp && (
        <button
          onClick={() => window.dispatchEvent(new Event('loom:open-help-manual'))}
          className="fixed bottom-[4.25rem] right-4 z-40 rounded-full border border-border bg-background px-2.5 py-1 text-[10.5px] font-medium text-muted-foreground shadow-md hover:bg-secondary hover:text-foreground"
          title="Open the Help Manual (F1)"
          data-testid="open-help-manual-btn"
        >
          Manual
        </button>
      )}
      {/* Floating AI Help button — hidden on The Loom workspace pages where
       * the ManuscriptToolbar button is more contextually placed. */}
      {!onLoomApp && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-4 right-4 z-40 flex h-11 w-11 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg hover:opacity-90"
          title="Ask AI (Cmd/Ctrl+/)"
          data-testid="ai-help-launcher"
        >
          <HelpCircle className="h-5 w-5" />
        </button>
      )}
      <AiHelpDialog open={open} onOpenChange={setOpen} />
    </>
  );
}
